import { useState } from "react";
import { CUSTOMER_STATE } from "./useEarlyAccessCustomerState";
import { useRuntimeConstants } from "../context/runtimeConstants/RuntimeConstantsContext";
import getWindow from "../utils/window";
import {
  fireJoinNowButtonClickAnalytics,
  fireNotifyMeButtonClickAnalytics,
} from "../analytics/pageInteraction";

type Window = {
  asos: {
    eventBus: {
      emit: (event: string, data: Record<string, unknown>) => void;
    };
  };
};

const showNotification = (type: "success" | "error", copy: string) => {
  (getWindow() as unknown as Window).asos.eventBus.emit("notificationBar", {
    type,
    copy,
  });
};

const showSuccessToast = (...messages: string[]) => {
  showNotification("success", messages.filter(Boolean).join(" "));
};

interface UseNotifyMeButtonProps {
  customerState: string;
  setCustomerState: (state: string) => void;
}

export function useNotifyMeButton({
  customerState,
  setCustomerState,
}: UseNotifyMeButtonProps) {
  const { getTranslation, isMixAndMatch } = useRuntimeConstants() as {
    getTranslation: (key: string) => string;
    isMixAndMatch: boolean;
  };

  const buttonText = getTranslation("pdp_eap_notify_me");
  const loyaltyToast = getTranslation("pdp_eap_loyalty_toast");
  const marketingToast = getTranslation("pdp_eap_marketing_toast");
  const errorToast = getTranslation("pdp_eap_generic_error");

  const [showJoinLoyaltyModal, setShowJoinLoyaltyModal] = useState(false);
  const [showMarketingOptInModal, setShowMarketingOptInModal] = useState(false);
  const [joinedWithoutConsent, setJoinedWithoutConsent] = useState(false);

  const handleClick = () => {
    if (
      customerState === CUSTOMER_STATE.LOYALTY_NONE_OPTED_OUT_MARKETING ||
      customerState === CUSTOMER_STATE.LOYALTY_NONE_OPTED_IN_MARKETING
    ) {
      setShowJoinLoyaltyModal(true);
    } else if (
      customerState === CUSTOMER_STATE.LOYALTY_VIEW_OPTED_OUT_MARKETING
    ) {
      setShowMarketingOptInModal(true);
    }
  };

  const handleJoinComplete = ({ consented }: { consented: boolean }) => {
    setShowJoinLoyaltyModal(false);
    fireJoinNowButtonClickAnalytics();
    if (
      consented ||
      customerState === CUSTOMER_STATE.LOYALTY_NONE_OPTED_IN_MARKETING
    ) {
      showSuccessToast(loyaltyToast, marketingToast);
      setCustomerState(CUSTOMER_STATE.LOYALTY_VIEW_OPTED_IN_MARKETING);
    } else {
      setJoinedWithoutConsent(true);
      setShowMarketingOptInModal(true);
    }
  };

  const handleMarketingOptInComplete = () => {
    setShowMarketingOptInModal(false);
    fireNotifyMeButtonClickAnalytics();
    if (joinedWithoutConsent) {
      showSuccessToast(loyaltyToast, marketingToast);
      setJoinedWithoutConsent(false);
    } else {
      showSuccessToast(marketingToast);
    }
    setCustomerState(CUSTOMER_STATE.LOYALTY_VIEW_OPTED_IN_MARKETING);
  };

  const handleMarketingModalClose = () => {
    setShowMarketingOptInModal(false);
    if (joinedWithoutConsent) {
      showSuccessToast(loyaltyToast);
      setJoinedWithoutConsent(false);
      setCustomerState(CUSTOMER_STATE.LOYALTY_VIEW_OPTED_OUT_MARKETING);
    }
  };

  const handleError = () => {
    showNotification("error", errorToast);
  };

  return {
    buttonText,
    isMixAndMatch,
    handleClick,
    joinLoyaltyModal: {
      open: showJoinLoyaltyModal,
      closeModal: () => setShowJoinLoyaltyModal(false),
      onJoin: handleJoinComplete,
      onError: handleError,
    },
    marketingModal: {
      open: showMarketingOptInModal,
      closeModal: handleMarketingModalClose,
      onOptIn: handleMarketingOptInComplete,
      onError: handleError,
    },
  };
}
